import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Zap, Shield, Clock, CheckCircle2, ArrowRight, Menu, X,
  FileText, Calendar, Mail, Users, BarChart3, Sparkles,
  MessageSquare, FolderOpen, Scale, Star, Play, ChevronRight
} from 'lucide-react';

const Lexy2Landing = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      
      {/* Clean Header - matches app style */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/lexy2')}>
            <div className="w-9 h-9 bg-gradient-to-br from-violet-600 to-violet-700 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/20">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold text-slate-900">Lexy</span>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">
              Funksjoner
            </a>
            <a href="#pricing" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">
              Priser
            </a>
            <a href="#testimonials" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">
              Kundeuttalelser
            </a>
            <Button
              onClick={() => navigate('/lexy/login')}
              variant="ghost"
              className="text-sm text-slate-600 hover:text-slate-900"
            >
              Logg inn
            </Button>
            <Button
              onClick={() => navigate('/lexy/signup')}
              className="bg-slate-900 hover:bg-slate-800 text-white text-sm px-5"
            >
              Kom i gang
            </Button>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-slate-100"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-100 bg-white px-6 py-4 space-y-3">
            <a href="#features" className="block text-slate-600 py-2">Funksjoner</a>
            <a href="#pricing" className="block text-slate-600 py-2">Priser</a>
            <a href="#testimonials" className="block text-slate-600 py-2">Kundeuttalelser</a>
            <div className="pt-3 space-y-2">
              <Button onClick={() => navigate('/lexy/login')} variant="outline" className="w-full">
                Logg inn
              </Button>
              <Button onClick={() => navigate('/lexy/signup')} className="w-full bg-slate-900">
                Kom i gang
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section - Clean & Modern */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-50 text-violet-700 rounded-full text-sm font-medium">
              <Sparkles className="w-4 h-4" />
              AI-drevet juridisk arbeidsflyt
            </div>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-center text-slate-900 mb-6 leading-tight tracking-tight">
            Din AI-assistent for
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-violet-500">
              advokatkontoret
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg text-slate-600 text-center max-w-2xl mx-auto mb-10">
            Lexy hjelper deg med saksbehandling, dokumenthåndtering, tidregistrering og klientkommunikasjon. 
            Spar tid på rutineoppgaver.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Button
              onClick={() => navigate('/lexy/signup')}
              size="lg"
              className="bg-slate-900 hover:bg-slate-800 text-white px-8 h-12 text-base"
            >
              Start gratis prøveperiode
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              onClick={() => {/* Demo video */}}
              size="lg"
              variant="outline"
              className="border-slate-200 hover:bg-slate-50 px-8 h-12 text-base"
            >
              <Play className="w-4 h-4 mr-2" />
              Se demo
            </Button>
          </div>

          {/* Dashboard Preview */}
          <div className="relative max-w-5xl mx-auto">
            {/* Browser frame */}
            <div className="bg-slate-900 rounded-t-xl px-4 py-3 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-slate-700"></div>
                <div className="w-3 h-3 rounded-full bg-slate-700"></div>
                <div className="w-3 h-3 rounded-full bg-slate-700"></div>
              </div>
              <div className="flex-1 mx-4">
                <div className="bg-slate-800 rounded-lg px-4 py-1.5 text-sm text-slate-400 text-center max-w-md mx-auto">
                  app.lexy.no/dashboard
                </div>
              </div>
            </div>
            
            {/* Dashboard mockup */}
            <div className="bg-slate-50 border-x border-b border-slate-200 rounded-b-xl overflow-hidden shadow-2xl shadow-slate-200/50">
              <div className="flex">
                {/* Sidebar */}
                <div className="w-16 bg-slate-900 min-h-[400px] p-3 flex flex-col items-center gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-violet-600 rounded-xl flex items-center justify-center">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-500 hover:bg-slate-800">
                    <FolderOpen className="w-5 h-5" />
                  </div>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-500 hover:bg-slate-800">
                    <Users className="w-5 h-5" />
                  </div>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-500 hover:bg-slate-800">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-500 hover:bg-slate-800">
                    <Mail className="w-5 h-5" />
                  </div>
                </div>
                
                {/* Main content */}
                <div className="flex-1 p-8 bg-white">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-slate-200 rounded-full"></div>
                      <div>
                        <h2 className="text-xl font-semibold text-slate-900">God morgen, Martin</h2>
                        <p className="text-sm text-slate-500">tirsdag 10. desember</p>
                      </div>
                    </div>
                    <div className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium">
                      + Ny sak
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-4 gap-4 mb-8">
                    {[
                      { value: '12', label: 'Aktive saker' },
                      { value: '3', label: 'Klienter' },
                      { value: '0', label: 'Oppgaver' },
                      { value: '0 kr', label: 'Ufakturert' }
                    ].map((stat, i) => (
                      <div key={i} className="text-center">
                        <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                        <div className="text-xs text-slate-500">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Lexy AI Card */}
                  <div className="bg-slate-50 border border-slate-100 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-violet-600 rounded-xl flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">Lexy</div>
                        <div className="text-xs text-slate-500">Spør Lexy</div>
                      </div>
                    </div>
                    <div className="text-slate-400 text-sm">Spør meg om hva som helst...</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Logo Cloud */}
      <section className="py-12 px-6 border-y border-slate-100 bg-slate-50/50">
        <div className="max-w-5xl mx-auto">
          <p className="text-center text-sm text-slate-500 mb-6">Brukes av ledende advokatfirmaer i Norge</p>
          <div className="flex flex-wrap justify-center items-center gap-x-12 gap-y-4 opacity-50">
            {['Thommessen', 'Wikborg Rein', 'BAHR', 'Hjort', 'Selmer'].map((name, i) => (
              <div key={i} className="text-lg font-semibold text-slate-400">{name}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section header */}
          <div className="text-center mb-16">
            <h2 className="text-base text-violet-600 font-semibold mb-3">Funksjoner</h2>
            <p className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Alt du trenger i én plattform
            </p>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Fra saksbehandling til fakturering - Lexy strømlinjeformer hele arbeidsflyten din.
            </p>
          </div>

          {/* Feature grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: <MessageSquare className="w-5 h-5" />,
                title: 'AI-assistent',
                description: 'Spør Lexy om juridiske spørsmål, få hjelp til dokumentutkast og analyser.'
              },
              {
                icon: <FolderOpen className="w-5 h-5" />,
                title: 'Saksbehandling',
                description: 'Organiser saker, oppgaver og dokumenter på ett sted med full oversikt.'
              },
              {
                icon: <FileText className="w-5 h-5" />,
                title: 'Dokumenter',
                description: 'Generer, rediger og versjonshåndter juridiske dokumenter med AI-støtte.'
              },
              {
                icon: <Calendar className="w-5 h-5" />,
                title: 'Kalender',
                description: 'Synkroniser med Outlook og Google Kalender. Hold oversikt over frister.'
              },
              {
                icon: <Mail className="w-5 h-5" />,
                title: 'Smart innboks',
                description: 'AI-drevet e-posttriage som kategoriserer og prioriterer meldinger.'
              },
              {
                icon: <Clock className="w-5 h-5" />,
                title: 'Tidregistrering',
                description: 'Automatisk tidsregistrering og enkel fakturering til klienter.'
              }
            ].map((feature, i) => (
              <div 
                key={i} 
                className="group p-6 bg-white border border-slate-100 rounded-2xl hover:border-slate-200 hover:shadow-lg hover:shadow-slate-100/50 transition-all"
              >
                <div className="w-11 h-11 bg-slate-100 group-hover:bg-violet-50 rounded-xl flex items-center justify-center text-slate-600 group-hover:text-violet-600 mb-4 transition-colors">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Feature Highlight */}
      <section className="py-24 px-6 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-violet-500/20 text-violet-400 rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                AI-drevet
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Spør Lexy om hva som helst
              </h2>
              <p className="text-lg text-slate-400 mb-8 leading-relaxed">
                Lexy forstår juridisk kontekst og hjelper deg med alt fra kontraktsanalyse 
                til prosedyre-spørsmål. Spar timer hver uke på rutineoppgaver.
              </p>
              
              <div className="space-y-4">
                {[
                  'Analyser dokumenter og kontrakter',
                  'Generer utkast til brev og avtaler',
                  'Søk i juridiske kilder og praksis',
                  'Oppsummer lange dokumenter'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-violet-400 flex-shrink-0" />
                    <span className="text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Chat preview */}
            <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-violet-600 rounded-xl flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-semibold">Lexy</div>
                  <div className="text-xs text-slate-500">AI-assistent</div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-slate-700/50 rounded-xl p-4">
                  <p className="text-sm text-slate-300">
                    Kan du analysere reklamasjonsfristen i denne kontrakten?
                  </p>
                </div>
                <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-4">
                  <p className="text-sm text-slate-300">
                    Basert på kontrakten har kjøper en reklamasjonsfrist på 14 dager fra 
                    leveringsdato for synlige mangler, og 2 år for skjulte mangler...
                  </p>
                </div>
              </div>
              
              <div className="mt-6 bg-slate-700/30 rounded-xl p-3 flex items-center gap-3">
                <input 
                  type="text" 
                  placeholder="Spør Lexy om noe..." 
                  className="flex-1 bg-transparent text-sm text-slate-400 outline-none"
                  disabled
                />
                <Button size="sm" className="bg-violet-600 hover:bg-violet-500">
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-base text-violet-600 font-semibold mb-3">Kundeuttalelser</h2>
            <p className="text-3xl sm:text-4xl font-bold text-slate-900">
              Advokater elsker Lexy
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                quote: 'Lexy har revolusjonert hvordan vi håndterer saksbehandling. Vi sparer flere timer hver dag.',
                name: 'Erik Hansen',
                role: 'Partner, Hansen & Co',
                avatar: 'EH'
              },
              {
                quote: 'AI-assistenten er fantastisk for dokumentanalyse. Den fanger opp ting vi ofte overser.',
                name: 'Maria Olsen',
                role: 'Advokat, Olsen Legal',
                avatar: 'MO'
              },
              {
                quote: 'Endelig en løsning som er designet for advokater. Intuitivt og kraftfullt.',
                name: 'Thomas Berg',
                role: 'Partner, Berg Advokatfirma',
                avatar: 'TB'
              }
            ].map((testimonial, i) => (
              <div key={i} className="bg-white border border-slate-100 rounded-2xl p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-slate-700 mb-6">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-sm font-semibold text-slate-600">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900 text-sm">{testimonial.name}</div>
                    <div className="text-slate-500 text-xs">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-base text-violet-600 font-semibold mb-3">Priser</h2>
            <p className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Enkel, transparent prissetting
            </p>
            <p className="text-lg text-slate-600">
              Ingen skjulte kostnader. Avbryt når som helst.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Starter */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Starter</h3>
              <p className="text-slate-500 text-sm mb-6">For enkeltadvokater</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-slate-900">1.490 kr</span>
                <span className="text-slate-500">/mnd</span>
              </div>
              <Button 
                onClick={() => navigate('/lexy/signup')}
                variant="outline" 
                className="w-full mb-6 h-11"
              >
                Start gratis prøveperiode
              </Button>
              <ul className="space-y-3">
                {[
                  '1 bruker',
                  'Ubegrenset saker',
                  'AI-assistent (100 forespørsler/mnd)',
                  'Dokumenthåndtering',
                  'Tidregistrering',
                  'E-post-integrasjon'
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-slate-400" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Pro */}
            <div className="bg-slate-900 text-white rounded-2xl p-8 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-violet-600 rounded-full text-xs font-medium">
                Mest populær
              </div>
              <h3 className="text-lg font-semibold mb-2">Pro</h3>
              <p className="text-slate-400 text-sm mb-6">For team og firmaer</p>
              <div className="mb-6">
                <span className="text-4xl font-bold">2.990 kr</span>
                <span className="text-slate-400">/bruker/mnd</span>
              </div>
              <Button 
                onClick={() => navigate('/lexy/signup')}
                className="w-full mb-6 h-11 bg-white text-slate-900 hover:bg-slate-100"
              >
                Start gratis prøveperiode
              </Button>
              <ul className="space-y-3">
                {[
                  'Ubegrenset brukere',
                  'Ubegrenset saker',
                  'AI-assistent (ubegrenset)',
                  'Avansert dokumentanalyse',
                  'Team-samarbeid',
                  'Kalender-synkronisering',
                  'API-tilgang',
                  'Prioritert support'
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-violet-400" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            Klar til å effektivisere advokatkontoret?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Start gratis i 14 dager. Ingen kredittkort nødvendig.
          </p>
          <Button
            onClick={() => navigate('/lexy/signup')}
            size="lg"
            className="bg-slate-900 hover:bg-slate-800 text-white px-8 h-12 text-base"
          >
            Start gratis prøveperiode
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-slate-900 text-slate-400">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-8">
            {/* Logo */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 bg-gradient-to-br from-violet-600 to-violet-700 rounded-xl flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg font-semibold text-white">Lexy</span>
              </div>
              <p className="text-sm text-slate-500 max-w-xs">
                AI-drevet juridisk arbeidsflyt for moderne advokatkontor.
              </p>
            </div>

            {/* Links */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-8">
              <div>
                <h4 className="text-sm font-semibold text-white mb-4">Produkt</h4>
                <ul className="space-y-2 text-sm">
                  <li><a href="#features" className="hover:text-white transition-colors">Funksjoner</a></li>
                  <li><a href="#pricing" className="hover:text-white transition-colors">Priser</a></li>
                  <li><a href="/lexy/login" className="hover:text-white transition-colors">Logg inn</a></li>
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white mb-4">Selskap</h4>
                <ul className="space-y-2 text-sm">
                  <li><a href="#" className="hover:text-white transition-colors">Om oss</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Kontakt</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Karriere</a></li>
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white mb-4">Juridisk</h4>
                <ul className="space-y-2 text-sm">
                  <li><a href="/privacy" className="hover:text-white transition-colors">Personvern</a></li>
                  <li><a href="/terms" className="hover:text-white transition-colors">Vilkår</a></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-slate-800 text-center text-sm text-slate-500">
            © 2025 Lexy AS. Alle rettigheter reservert.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Lexy2Landing;
